import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import type { WithTestId } from 'types/app';

import { analytics } from '@Modules/analytics/analytics';
import type { OfferType, PriceType } from '@Modules/offers/types';

import { getMonthlyLoanPayment } from '@Atomic/organisms/Loan/utils';
import { Button } from '@Atomic/atoms/Button';
import { Title } from '@Atomic/atoms/Headline';
import { Text } from '@Atomic/atoms/Text';

import styles from './style.module.scss';

type LoanViewProps = {
  offerId: OfferType['id'];
  className?: string;
  price: PriceType;
  url?: string;
};

export const LoanView: FC<WithTestId<LoanViewProps>> = ({
  offerId,
  price,
  testId = 'loan',
  className,
  url,
}) => {
  const { t } = useTranslation();

  if (!url) {
    return null;
  }

  const priceFormatted = getMonthlyLoanPayment(price, t);

  const handleButtonClick = (): void => {
    analytics.viewOffer.ipotekaBankRequest({
      offer_id: `${offerId}`,
    });
  };

  return (
    <div className={cn(styles.loan, className)} data-testid={testId}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.info}>
            <div className={styles.labels}>
              <Title.H3 className={styles.amount}>
                {priceFormatted} / {t('loan.period')}
              </Title.H3>
              <Text className={styles.description} variant="large">
                <span>{t('loan.loanType')}</span>
              </Text>
            </div>
            <Text className={styles.disclaimer} variant="small">
              {t('loan.advertisement')}
            </Text>
          </div>
          <div className={styles.logo}>
            <img
              src="https://file.birbir.uz/web/frontend/OTP.png"
              alt="Ipoteka Bank"
              className={styles.logoImage}
            />
          </div>
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          className={styles.applyButton}
          intent="success"
          presentation="tonal"
          to={url}
          target="_blank"
          rel="noopener noreferrer"
          testId={`${testId}-apply`}
          fullWidth
          onClick={handleButtonClick}
        >
          {t('loan.applyButton')}
        </Button>
      </div>
    </div>
  );
};
